import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useTranslation } from 'react-i18next';

import { getChannelDisplayLabel } from '@/entities/channel';
import {
  CompanyChatAccessChannelDto,
  CompanyChatAccessUserDto,
} from '@/shared/api/companies';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { Notification } from '@/shared/ui';

import {
  useCompanyChatAccess,
  useUpdateCompanyChatAccess,
} from '../model/useCompanyChatAccess';
import { canReceiveNewClients } from '../model/chatAccess';

function getUserName(user: CompanyChatAccessUserDto): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || user.id;
}

function getUserInitials(user: CompanyChatAccessUserDto): string {
  const name = [user.firstName, user.lastName].filter(Boolean);
  if (name.length > 0) {
    return name
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }
  return (user.email || user.id).slice(0, 2).toUpperCase();
}

function getChannelLabel(channel: CompanyChatAccessChannelDto): string {
  return getChannelDisplayLabel(channel);
}

function normalize(user: CompanyChatAccessUserDto): CompanyChatAccessUserDto {
  const channelIds = [...new Set(user.channelIds)];
  return {
    ...user,
    channelIds,
    receivesNewClients: canReceiveNewClients({ channelIds }) ? user.receivesNewClients : false,
  };
}

function toEditableUsers(users: CompanyChatAccessUserDto[]) {
  return users.map((user) =>
    normalize({
      ...user,
      channelIds: [...user.channelIds],
    })
  );
}

export const CompanyChatAccessSettings = ({
  companyId,
}: {
  companyId: string;
}) => {
  const { t } = useTranslation();
  const { data, isPending, isError, error } = useCompanyChatAccess(companyId);
  const updateMutation = useUpdateCompanyChatAccess(companyId);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<CompanyChatAccessUserDto[]>([]);

  useEffect(() => {
    if (data?.users) setUsers(toEditableUsers(data.users));
  }, [data]);

  const channelById = useMemo(
    () =>
      new Map((data?.channels ?? []).map((channel) => [channel.id, channel])),
    [data?.channels]
  );

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) =>
      [getUserName(user), user.email, user.id]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [search, users]);

  const updateUser = (
    userId: string,
    updater: (user: CompanyChatAccessUserDto) => CompanyChatAccessUserDto
  ) => {
    setUsers((current) =>
      current.map((user) => (user.id === userId ? updater(user) : user))
    );
  };

  const setReceivesForAll = (checked: boolean) => {
    setUsers((current) =>
      current.map((user) => ({
        ...user,
        receivesNewClients: canReceiveNewClients(user) ? checked : false,
      }))
    );
  };

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      users: users.map(normalize).map((user) => ({
        userId: user.id,
        receivesNewClients: user.receivesNewClients,
        channelIds: user.channelIds,
      })),
    });
  };

  const handleCancel = () => {
    if (data?.users) setUsers(toEditableUsers(data.users));
  };

  if (isPending) return <Loader containerSize="medium" />;

  if (isError) {
    return (
      <>
        <NotFoundData title={t('company:chatAccess.loadError')} />
        <Notification open={isError} title={error?.message} />
      </>
    );
  }

  if (!data) return <NotFoundData title={t('company:chatAccess.empty')} />;

  const hasReceivers = users.some(canReceiveNewClients);
  const allReceive =
    hasReceivers &&
    users.filter(canReceiveNewClients).every((user) => user.receivesNewClients);

  return (
    <Box sx={{ mt: 4 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 36,
                height: 36,
                borderRadius: 2,
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <TuneOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {t('company:chatAccess.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('company:chatAccess.description')}
              </Typography>
            </Box>
          </Stack>
        </Stack>
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('company:chatAccess.search')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: { xs: '100%', md: 320 },
            '& .MuiOutlinedInput-root': {
              bgcolor: 'common.white',
              borderRadius: 3,
            },
          }}
        />
      </Stack>

      <Box
        sx={{
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.14),
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: 'common.white',
          boxShadow: '0 18px 44px rgba(28, 29, 34, 0.06)',
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            background:
              'linear-gradient(135deg, rgba(0,120,212,0.08), rgba(22,132,91,0.06) 58%, rgba(154,106,34,0.08))',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.82)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <GroupsOutlinedIcon sx={{ color: 'primary.main' }} />
                <Box>
                  <Typography fontWeight={700}>
                    {t('company:chatAccess.applyForAll')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('company:chatAccess.bulkDescription')}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{
                  mt: 0.5,
                  p: 1.25,
                  borderRadius: 2.5,
                  bgcolor: (theme) => alpha(theme.palette.actions.go, 0.1),
                }}
              >
                <Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography fontWeight={700}>
                      {t('company:chatAccess.distribution')}
                    </Typography>
                    <Tooltip title={t('company:chatAccess.receiveHint')} arrow>
                      <IconButton size="small" sx={{ p: 0.25 }}>
                        <HelpOutlineIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {hasReceivers
                      ? t('company:chatAccess.bulkDistributionHint')
                      : t('company:chatAccess.bulkDistributionDisabled')}
                  </Typography>
                </Box>
                <Switch
                  checked={allReceive}
                  disabled={!hasReceivers}
                  onChange={(event) => setReceivesForAll(event.target.checked)}
                />
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Stack spacing={1.25} sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
            sx={{ px: { xs: 0.5, md: 1 } }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('company:chatAccess.usersFound', {
                count: filteredUsers.length,
              })}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={t('company:chatAccess.selectedChannels', {
                  count: data.channels.length,
                })}
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              />
              <Chip
                size="small"
                label={t('company:chatAccess.receiversCount', {
                  count: users.filter((user) => user.receivesNewClients).length,
                })}
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.actions.go, 0.12),
                }}
              />
            </Stack>
          </Stack>

          {filteredUsers.map((user) => {
            const canReceive = canReceiveNewClients(user);

            return (
              <Box
                key={user.id}
                sx={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'minmax(190px, 0.75fr) minmax(300px, 1fr) 166px',
                  },
                  gap: { xs: 1.75, md: 2 },
                  alignItems: 'center',
                  p: { xs: 1.5, md: 1.75 },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  bgcolor: 'grey.50',
                  transition:
                    'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 14,
                    bottom: 14,
                    left: 0,
                    width: 4,
                    borderRadius: '0 999px 999px 0',
                    bgcolor: 'primary.main',
                    opacity: 0.28,
                  },
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 16px 32px rgba(0,120,212,0.1)',
                  },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 42,
                      height: 42,
                      fontSize: 14,
                      fontWeight: 800,
                      color: 'primary.main',
                      bgcolor: 'common.white',
                      border: '1px solid',
                      borderColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.24),
                    }}
                  >
                    {getUserInitials(user)}
                  </Avatar>
                  <Box minWidth={0}>
                    <Typography fontWeight={700} noWrap>
                      {getUserName(user)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {user.email || user.id}
                    </Typography>
                  </Box>
                </Stack>

                <FormControl size="small" fullWidth>
                  <InputLabel id={`${user.id}-channels-label`}>
                    {t('company:chatAccess.channelsLabel')}
                  </InputLabel>
                  <Select
                    labelId={`${user.id}-channels-label`}
                    multiple
                    value={user.channelIds}
                    input={
                      <OutlinedInput
                        label={t('company:chatAccess.channelsLabel')}
                      />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('company:chatAccess.channelsEmpty')}
                          </Typography>
                        ) : (
                          selected.map((channelId) => (
                            <Chip
                              key={channelId}
                              size="small"
                              label={
                                channelById.has(channelId)
                                  ? getChannelLabel(channelById.get(channelId)!)
                                  : channelId
                              }
                              sx={{
                                maxWidth: 188,
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.1),
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                },
                              }}
                            />
                          ))
                        )}
                      </Box>
                    )}
                    onChange={(event) => {
                      const value = event.target.value;
                      const channelIds =
                        typeof value === 'string' ? value.split(',') : value;
                      updateUser(user.id, (current) =>
                        normalize({
                          ...current,
                          channelIds,
                        })
                      );
                    }}
                    sx={{
                      bgcolor: 'common.white',
                      '& .MuiSelect-select': {
                        minHeight: 32,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  >
                    {data.channels.map((channel) => (
                      <MenuItem key={channel.id} value={channel.id}>
                        {getChannelLabel(channel)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
                  sx={{
                    justifySelf: { xs: 'stretch', md: 'end' },
                    width: { xs: '100%', md: 158 },
                    p: 1.25,
                    borderRadius: 3,
                    bgcolor: canReceive
                      ? (theme) => alpha(theme.palette.actions.go, 0.1)
                      : 'rgba(28,29,34,0.04)',
                    border: '1px solid',
                    borderColor: canReceive
                      ? (theme) => alpha(theme.palette.actions.go, 0.2)
                      : 'divider',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box minWidth={0}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={800}
                        noWrap
                      >
                        {t('company:chatAccess.leadsShort')}
                      </Typography>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {canReceive
                          ? user.receivesNewClients
                            ? t('company:chatAccess.distributionOn')
                            : t('company:chatAccess.distributionOff')
                          : t('company:chatAccess.distributionDisabled')}
                      </Typography>
                    </Box>
                    <Switch
                      sx={{ flexShrink: 0 }}
                      checked={canReceive && user.receivesNewClients}
                      disabled={!canReceive}
                      onChange={(event) =>
                        updateUser(user.id, (current) => ({
                          ...current,
                          receivesNewClients: event.target.checked,
                        }))
                      }
                    />
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{ mt: 2 }}
      >
        <Button variant="text" color="secondary" onClick={handleCancel}>
          {t('company:chatAccess.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {t('company:chatAccess.apply')}
        </Button>
      </Stack>
      <Notification
        open={updateMutation.isError}
        title={updateMutation.error?.message}
      />
    </Box>
  );
};
